import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaTrash, FaSave, FaFileInvoiceDollar, FaEdit, FaFilePdf, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../assets/logo.png';
import { safeFetch } from '../utils/apiUtils';
import './AdminInvoice.css';

const AdminInvoice = () => {
    const generateInvoiceNumber = () => {
        return `INV-${Math.floor(1000 + Math.random() * 9000)}`;
    };

    const emptyInvoice = {
        invoice_number: generateInvoiceNumber(),
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        discount: 0,
        paid_amount: 0,
        payment_status: 'unpaid',
        payment_method: 'Cash',
        notes: ''
    };

    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
    const [invoicesList, setInvoicesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [invoice, setInvoice] = useState(emptyInvoice);
    const [items, setItems] = useState([
        { id: Date.now(), item_name: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }
    ]);

    useEffect(() => {
        if (viewMode === 'list') {
            fetchInvoices();
        }
    }, [viewMode]);

    const fetchInvoices = async () => {
        setIsLoading(true);
        try {
            const { ok, data } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/invoices', {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (ok) {
                // Determine if structure is wrapped under 'invoices', 'data', or is the raw array
                let fetchedInvoices = data?.invoices || data?.data || data;
                if (!Array.isArray(fetchedInvoices)) fetchedInvoices = [];
                setInvoicesList(fetchedInvoices);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            // Non-blocking catch
        } finally {
            setIsLoading(false);
        }
    };

    const handleInvoiceChange = (e) => {
        const { name, value } = e.target;
        setInvoice(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        if (field === 'quantity' || field === 'unit_price') {
            const qty = parseFloat(newItems[index].quantity) || 0;
            const price = parseFloat(newItems[index].unit_price) || 0;
            newItems[index].total_price = qty * price;
        }
        setItems(newItems);
    };

    const addItem = () => {
        setItems([
            ...items,
            { id: Date.now(), item_name: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }
        ]);
    };

    const removeItem = (id) => {
        if (items.length > 1) {
            setItems(items.filter(item => item.id !== id));
        } else {
            toast.error("You must have at least one item.");
        }
    };

    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
    const discount = parseFloat(invoice.discount) || 0;
    const totalAmount = subtotal - discount;

    const generatePDF = async (invoiceData, itemsData) => {
        const doc = new jsPDF();

        let logoImg = null;
        try {
            logoImg = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = logo;
            });
        } catch (e) {
            console.error('Error loading logo', e);
        }

        doc.setFillColor(59, 130, 246);
        doc.rect(0, 0, 210, 50, 'F');

        if (logoImg) {
            const imgWidth = 45;
            const imgHeight = (logoImg.height * imgWidth) / logoImg.width;
            doc.addImage(logoImg, 'PNG', 14, 15, imgWidth, imgHeight);
        } else {
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(22);
            doc.setFont("helvetica", "bold");
            doc.text("Nimai Solutions", 14, 25);
        }

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(26);
        doc.setFont("helvetica", "bold");
        doc.text("INVOICE", 150, 25);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`NUM: ${invoiceData.invoice_number}`, 150, 33);
        doc.text(`DATE: ${invoiceData.invoice_date}`, 150, 39);
        doc.text(`DUE: ${invoiceData.due_date}`, 150, 45);

        doc.setTextColor(30, 41, 59);

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Bill To:", 14, 65);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Name: ${invoiceData.customer_name}`, 14, 72);
        if (invoiceData.customer_email) doc.text(`Email: ${invoiceData.customer_email}`, 14, 78);
        if (invoiceData.customer_phone) doc.text(`Phone: ${invoiceData.customer_phone}`, 14, 84);
        if (invoiceData.customer_address) {
            const addrY = invoiceData.customer_phone ? 90 : (invoiceData.customer_email ? 84 : 78);
            const splitAddress = doc.splitTextToSize(`Address: ${invoiceData.customer_address}`, 80);
            doc.text(splitAddress, 14, addrY);
        }

        const startYForTable = 105;
        const tableColumn = ["Item Name", "Description", "Quantity", "Price", "Total"];
        const tableRows = itemsData.map(item => [
            item.item_name,
            item.description,
            item.quantity,
            `INR ${parseFloat(item.unit_price).toFixed(2)}`,
            `INR ${parseFloat(item.total_price).toFixed(2)}`
        ]);

        autoTable(doc, {
            startY: startYForTable,
            head: [tableColumn],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [59, 130, 246] },
            margin: { top: 20 }
        });

        const finalY = (doc.lastAutoTable?.finalY || startYForTable + 20) + 10;

        const invoiceSubtotal = itemsData.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
        const invoiceTotalAmount = invoiceSubtotal - (parseFloat(invoiceData.discount) || 0);

        doc.setFont("helvetica", "normal");
        doc.text(`Subtotal:`, 130, finalY);
        doc.text(`INR ${invoiceSubtotal.toFixed(2)}`, 165, finalY);

        doc.text(`Discount:`, 130, finalY + 6);
        doc.text(`- INR ${parseFloat(invoiceData.discount || 0).toFixed(2)}`, 165, finalY + 6);

        doc.setFont("helvetica", "bold");
        doc.text(`Total Amount:`, 130, finalY + 14);
        doc.text(`INR ${invoiceTotalAmount.toFixed(2)}`, 165, finalY + 14);

        doc.setFont("helvetica", "normal");
        doc.text(`Amount Paid:`, 130, finalY + 22);
        doc.text(`INR ${parseFloat(invoiceData.paid_amount || 0).toFixed(2)}`, 165, finalY + 22);

        doc.setFont("helvetica", "bold");
        doc.text(`Balance Due:`, 130, finalY + 28);
        const balanceDue = Math.max(0, invoiceTotalAmount - (parseFloat(invoiceData.paid_amount) || 0));
        doc.text(`INR ${balanceDue.toFixed(2)}`, 165, finalY + 28);

        if (invoiceData.notes) {
            doc.setFont("helvetica", "bold");
            doc.text("Notes:", 14, finalY + 10);
            doc.setFont("helvetica", "normal");
            const splitNotes = doc.splitTextToSize(invoiceData.notes, 100);
            doc.text(splitNotes, 14, finalY + 16);
        }

        doc.setFont("helvetica", "normal");
        doc.text(`Payment Status: ${invoiceData.payment_status.toUpperCase()}`, 14, finalY + 36);
        doc.text(`Payment Method: ${invoiceData.payment_method}`, 14, finalY + 42);

        const pageHeight = doc.internal.pageSize.height || 297;
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont("helvetica", "italic");
        doc.text("This is a Computer Generated Invoice. No Physical Signature is Required.", 105, pageHeight - 15, { align: "center" });

        doc.setFont("helvetica", "bold");
        doc.text("Nimai Solutions", 105, pageHeight - 10, { align: "center" });

        doc.save(`${invoiceData.invoice_number}.pdf`);
        toast.success(`Invoice ${invoiceData.invoice_number} saved as PDF!`);
    };

    const handleSaveAndPDF = async (e) => {
        e.preventDefault();

        if (!invoice.customer_name) {
            toast.error("Customer name is required.");
            return;
        }

        const invoicePayload = {
            ...invoice,
            subtotal: subtotal.toFixed(2),
            total_amount: totalAmount.toFixed(2),
            items: items.map(item => ({
                item_name: item.item_name,
                description: item.description,
                quantity: item.quantity,
                unit_price: item.unit_price,
                total_price: (parseFloat(item.total_price) || 0).toFixed(2)
            }))
        };

        const url = editingId
            ? `https://aayushrealproperties.in/nimaibackend/public/api/invoices/${editingId}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/invoices';
        const method = editingId ? 'PUT' : 'POST';

        // Trigger local PDF first so it doesnt block waiting on network if needed
        await generatePDF(invoicePayload, invoicePayload.items);

        try {
            const { ok, data } = await safeFetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                },
                body: JSON.stringify(invoicePayload)
            });

            if (ok) {
                toast.success(`Invoice ${editingId ? 'updated' : 'created'} securely in backend.`);
                // Return to list view
                setInvoice(emptyInvoice);
                setItems([{ id: Date.now(), item_name: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }]);
                setEditingId(null);
                setViewMode('list');
            } else {
                toast.error(data?.message || 'Failed to save invoice fully online, PDF saved.');
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Network Error. Document only saved locally.');
        }
    };

    const handleEdit = async (inv) => {
        setIsLoading(true);
        // Sometimes backend returns items, if not we rely on fetching specific id
        try {
            const { ok, data } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/invoices/${inv.id}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (ok) {
                const fetchedInv = data.invoice || data.data || data;
                setInvoice({
                    invoice_number: fetchedInv.invoice_number || generateInvoiceNumber(),
                    customer_name: fetchedInv.customer_name || '',
                    customer_email: fetchedInv.customer_email || '',
                    customer_phone: fetchedInv.customer_phone || '',
                    customer_address: fetchedInv.customer_address || '',
                    invoice_date: fetchedInv.invoice_date || new Date().toISOString().split('T')[0],
                    due_date: fetchedInv.due_date || '',
                    discount: fetchedInv.discount || 0,
                    paid_amount: fetchedInv.paid_amount || 0,
                    payment_status: fetchedInv.payment_status || 'unpaid',
                    payment_method: fetchedInv.payment_method || 'Cash',
                    notes: fetchedInv.notes || ''
                });

                if (fetchedInv.items && fetchedInv.items.length > 0) {
                    setItems(fetchedInv.items.map(item => ({
                        id: item.id || Date.now() + Math.random(),
                        item_name: item.item_name || '',
                        description: item.description || '',
                        quantity: item.quantity || 1,
                        unit_price: item.unit_price || 0,
                        total_price: item.total_price || 0
                    })));
                } else {
                    setItems([{ id: Date.now(), item_name: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }]);
                }

                setEditingId(inv.id);
                setViewMode('form');
            } else {
                toast.error('Failed to grab full invoice details.');
            }
        } catch (err) {
            toast.error('Network Error getting details.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this invoice?")) return;

        try {
            const { ok } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/invoices/${id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (ok) {
                toast.success('Invoice deleted successfully');
                fetchInvoices();
            } else {
                toast.error('Failed to delete.');
            }
        } catch (err) {
            toast.error('Deletion network error.');
        }
    };

    const handleAddNew = () => {
        setInvoice({ ...emptyInvoice, invoice_number: generateInvoiceNumber() });
        setItems([{ id: Date.now(), item_name: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }]);
        setEditingId(null);
        setViewMode('form');
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>{viewMode === 'list' ? 'Invoice' : 'Generate'} <span>{viewMode === 'list' ? 'Manager' : 'Invoice'}</span></h1>
                        <p>{viewMode === 'list' ? 'Manage your saved invoices securely.' : 'Create and manage client invoices easily.'}</p>
                    </div>
                    {viewMode === 'list' ? (
                        <div className="admin-actions">
                            <button className="btn-add-stat" onClick={handleAddNew}>
                                <FaPlus /> Create Invoice
                            </button>
                        </div>
                    ) : (
                        <div className="admin-actions">
                            <button className="btn-cancel" onClick={() => setViewMode('list')} style={{ background: '#f1f5f9', color: '#0f172a', border: 'none', padding: '10px 15px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                                <FaArrowLeft /> Back to List
                            </button>
                        </div>
                    )}
                </header>

                <AnimatePresence mode="wait">
                    {viewMode === 'list' && (
                        <motion.div
                            key="list-view"
                            className="invoice-container"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                        >
                            <div className="items-table-container">
                                {isLoading ? (
                                    <div style={{ padding: '30px', textAlign: 'center' }}>Loading invoices...</div>
                                ) : (
                                    <table className="items-table">
                                        <thead>
                                            <tr>
                                                <th>INV Number</th>
                                                <th>Client Info</th>
                                                <th>Date</th>
                                                <th>Total</th>
                                                <th>Status</th>
                                                <th style={{ textAlign: 'center' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoicesList.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No invoices created yet.</td>
                                                </tr>
                                            ) : (
                                                invoicesList.map((inv) => (
                                                    <tr key={inv.id}>
                                                        <td style={{ fontWeight: 'bold', color: '#3b82f6' }}>{inv.invoice_number}</td>
                                                        <td>
                                                            <div>{inv.customer_name}</div>
                                                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{inv.customer_email || 'No email'}</div>
                                                        </td>
                                                        <td>{inv.invoice_date}</td>
                                                        <td style={{ fontWeight: 'bold' }}>₹{inv.total_amount}</td>
                                                        <td>
                                                            <span style={{
                                                                padding: '5px 10px',
                                                                borderRadius: '20px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'bold',
                                                                background: inv.payment_status === 'paid' ? '#dcfce7' : inv.payment_status === 'partial' ? '#fef9c3' : '#fee2e2',
                                                                color: inv.payment_status === 'paid' ? '#16a34a' : inv.payment_status === 'partial' ? '#ca8a04' : '#dc2626'
                                                            }}>
                                                                {inv.payment_status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                onClick={() => handleEdit(inv)}
                                                                style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', margin: '0 8px', fontSize: '1.2rem' }}
                                                                title="Edit & Resave PDF"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(inv.id)}
                                                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', margin: '0 8px', fontSize: '1.2rem' }}
                                                                title="Delete"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {viewMode === 'form' && (
                        <motion.div
                            key="form-view"
                            className="invoice-container"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                        >
                            <div className="invoice-header-actions">
                                <h2><FaFileInvoiceDollar /> {editingId ? 'Edit Invoice Details' : 'New Invoice Details'}</h2>
                            </div>

                            <form onSubmit={handleSaveAndPDF}>
                                <div className="form-section">
                                    <h3 className="form-section-title">Invoice Information</h3>
                                    <div className="form-grid-3">
                                        <div className="form-group">
                                            <label>Invoice Number</label>
                                            <input type="text" name="invoice_number" value={invoice.invoice_number} readOnly className="read-only" style={{ backgroundColor: '#e2e8f0', color: '#475569' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Invoice Date</label>
                                            <input type="date" name="invoice_date" value={invoice.invoice_date} onChange={handleInvoiceChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Due Date</label>
                                            <input type="date" name="due_date" value={invoice.due_date} onChange={handleInvoiceChange} required />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="form-section-title">Client Information</h3>
                                    <div className="form-grid-3">
                                        <div className="form-group">
                                            <label>Customer Name</label>
                                            <input type="text" name="customer_name" value={invoice.customer_name} onChange={handleInvoiceChange} placeholder="Enter Client Name" required />
                                        </div>
                                        <div className="form-group">
                                            <label>Emal Address</label>
                                            <input type="email" name="customer_email" value={invoice.customer_email} onChange={handleInvoiceChange} placeholder="Client Email (Optional)" />
                                        </div>
                                        <div className="form-group">
                                            <label>Phone Number</label>
                                            <input type="tel" name="customer_phone" value={invoice.customer_phone} onChange={handleInvoiceChange} placeholder="Client Phone (Optional)" />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Billing Address</label>
                                        <textarea name="customer_address" value={invoice.customer_address} onChange={handleInvoiceChange} placeholder="Enter Billing Address" required></textarea>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="form-section-title">Invoice Items</h3>
                                    <div className="items-table-container">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>Item / Service Name</th>
                                                    <th>Description</th>
                                                    <th className="qty-col">Qty</th>
                                                    <th className="price-col">Price</th>
                                                    <th className="total-col">Total</th>
                                                    <th style={{ width: '50px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                placeholder="Service Name"
                                                                value={item.item_name}
                                                                onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                                                                required
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                placeholder="Description (Optional)"
                                                                value={item.description}
                                                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="qty-col">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="0.01"
                                                                value={item.quantity}
                                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                                required
                                                            />
                                                        </td>
                                                        <td className="price-col">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={item.unit_price}
                                                                onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                                required
                                                            />
                                                        </td>
                                                        <td className="total-col">
                                                            ₹{parseFloat(item.total_price).toFixed(2)}
                                                        </td>
                                                        <td>
                                                            <button type="button" className="btn-remove-item" onClick={() => removeItem(item.id)}>
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button type="button" className="btn-add-item" onClick={addItem}>
                                        <FaPlus /> Add Line Item
                                    </button>
                                </div>

                                <div className="form-section">
                                    <div className="form-grid-2">
                                        <div>
                                            <h3 className="form-section-title">Payment Details</h3>
                                            <div className="form-grid-2">
                                                <div className="form-group">
                                                    <label>Payment Status</label>
                                                    <select name="payment_status" value={invoice.payment_status} onChange={handleInvoiceChange}>
                                                        <option value="unpaid">Unpaid</option>
                                                        <option value="partial">Partial</option>
                                                        <option value="paid">Paid</option>
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <label>Payment Method</label>
                                                    <select name="payment_method" value={invoice.payment_method} onChange={handleInvoiceChange}>
                                                        <option value="System">Select Method</option>
                                                        <option value="Cash">Cash</option>
                                                        <option value="UPI">UPI / NetBanking</option>
                                                        <option value="Card">Credit / Debit Card</option>
                                                        <option value="Bank Transfer">Bank Transfer</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label>Paid Amount</label>
                                                <input type="number" name="paid_amount" min="0" step="0.01" value={invoice.paid_amount} onChange={handleInvoiceChange} placeholder="Enter Received Amount" />
                                            </div>

                                            <div className="form-group">
                                                <label>Additional Notes</label>
                                                <textarea name="notes" value={invoice.notes} onChange={handleInvoiceChange} placeholder="Message or terms & conditions..." rows="3"></textarea>
                                            </div>
                                        </div>

                                        <div className="invoice-summary">
                                            <div className="summary-box">
                                                <div className="summary-row">
                                                    <span>Subtotal</span>
                                                    <span>₹{subtotal.toFixed(2)}</span>
                                                </div>
                                                <div className="summary-row">
                                                    <span>Discount</span>
                                                    <input
                                                        type="number"
                                                        name="discount"
                                                        min="0"
                                                        step="0.01"
                                                        value={invoice.discount}
                                                        onChange={handleInvoiceChange}
                                                    />
                                                </div>
                                                <div className="summary-row total">
                                                    <span>Total Amount</span>
                                                    <span>₹{totalAmount.toFixed(2)}</span>
                                                </div>
                                                <div className="summary-row" style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                                                    <span>Amount Paid</span>
                                                    <span style={{ color: '#10b981' }}>₹{parseFloat(invoice.paid_amount || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="summary-row" style={{ fontWeight: '500' }}>
                                                    <span>Balance Due</span>
                                                    <span style={{ color: '#ef4444' }}>₹{Math.max(0, totalAmount - (parseFloat(invoice.paid_amount) || 0)).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoice-actions">
                                    <button type="button" className="btn-cancel" onClick={() => setViewMode('list')}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-generate">
                                        <FaSave /> {editingId ? 'Update & Save PDF' : 'Save Invoice & PDF'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminInvoice;
